import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { CalendarRange } from 'lucide-react';

export const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const pad = (n) => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(y, m, 0).getDate();
export const fmtDateEs = (iso) => (iso ? iso.split('-').reverse().join('/') : '…');

export const defaultFinancePeriod = () => {
  const now = new Date();
  return { mode: 'year', year: now.getFullYear(), month: now.getMonth() + 1, from: `${now.getFullYear()}-01-01`, to: now.toISOString().slice(0, 10) };
};

// Devuelve { from, to, label } (fechas ISO inclusivas) para el período seleccionado
export const resolveFinancePeriod = (p) => {
  if (p.mode === 'month') {
    const from = `${p.year}-${pad(p.month)}-01`;
    return { from, to: `${p.year}-${pad(p.month)}-${pad(lastDay(p.year, p.month))}`, label: `${MONTHS_ES[p.month - 1]} ${p.year}` };
  }
  if (p.mode === 'range') {
    return { from: p.from || '0000-01-01', to: p.to || '9999-12-31', label: `${fmtDateEs(p.from)} – ${fmtDateEs(p.to)}` };
  }
  return { from: `${p.year}-01-01`, to: `${p.year}-12-31`, label: `Año ${p.year}` };
};

// years: años con datos; monthsWithData: Set de 'YYYY-MM' con datos (banco o Quipu)
export function FinancePeriodSelector({ period, onChange, years, monthsWithData, coverage, testPrefix = 'period' }) {
  const { label } = resolveFinancePeriod(period);
  const set = (patch) => onChange({ ...period, ...patch });
  const hasMonth = (m) => !monthsWithData || monthsWithData.has(`${period.year}-${pad(m)}`);
  return (
    <div className="flex flex-col gap-1.5" data-testid={`${testPrefix}-selector`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1">
          <CalendarRange className="w-4 h-4" /> Período
        </div>
        <Select value={period.mode} onValueChange={(mode) => set({ mode })}>
          <SelectTrigger className="h-9 w-[120px]" data-testid={`${testPrefix}-mode`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="year">Año</SelectItem>
            <SelectItem value="month">Mes</SelectItem>
            <SelectItem value="range">Rango</SelectItem>
          </SelectContent>
        </Select>
        {period.mode !== 'range' && (
          <Select value={String(period.year)} onValueChange={(y) => set({ year: Number(y) })}>
            <SelectTrigger className="h-9 w-[100px]" data-testid={`${testPrefix}-year`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {period.mode === 'month' && (
          <Select value={String(period.month)} onValueChange={(m) => set({ month: Number(m) })}>
            <SelectTrigger className="h-9 w-[150px]" data-testid={`${testPrefix}-month`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS_ES.map((name, i) => (
                <SelectItem key={name} value={String(i + 1)} disabled={!hasMonth(i + 1)} data-testid={`${testPrefix}-month-${i + 1}`}>
                  {name}{!hasMonth(i + 1) ? ' · sin datos' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {period.mode === 'range' && (
          <>
            <Input type="date" className="h-9 w-[150px]" value={period.from} onChange={(e) => set({ from: e.target.value })} data-testid={`${testPrefix}-from`} />
            <span className="text-muted-foreground text-sm">→</span>
            <Input type="date" className="h-9 w-[150px]" value={period.to} onChange={(e) => set({ to: e.target.value })} data-testid={`${testPrefix}-to`} />
          </>
        )}
        <Badge className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/10 ml-1" data-testid={`${testPrefix}-label`}>{label}</Badge>
      </div>
      {coverage && (
        <p className="text-[11px] text-muted-foreground" data-testid={`${testPrefix}-coverage`}>{coverage}</p>
      )}
    </div>
  );
}
